import React, { useEffect, useState } from "react";
import { FiArrowLeft, FiArrowRight, FiBarChart2, FiRefreshCw, FiSearch } from "react-icons/fi";
import { useSearchParams } from "react-router-dom";
import axios from "../../api/axiosInstance";
import { usePmsTheme } from "../../Components/PmsThemeContext";
import { readStoredUser } from "../../utils/sessionUser";
import useCrossTabDataRefreshToken from "../../hooks/useCrossTabDataRefresh";
import usePurchasingReadAccess from "../components/usePurchasingReadAccess";
import { getDocumentUnitName } from "../utils/unitNames";
import { reportsText } from "./reportsMessages";
import "../PurchasingUi.css";
import "./Reports.css";
import "./ItemLastUnitCostReport.css";

const PAGE_SIZE = 50;
const REPORT_VIEW = "purchasing.reports.items_purchased.view";
const SOURCES = { purchase: "Purchase", inventory_adjustment: "Inventory adjustment", opening_balance: "Opening balance" };

function costDate(value, locale) {
  const dateOnly = String(value || "").slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(dateOnly)) return null;
  const date = new Date(`${dateOnly}T00:00:00Z`);
  return Number.isNaN(date.getTime()) || date.toISOString().slice(0, 10) !== dateOnly
    ? null : date.toLocaleDateString(locale, { timeZone: "UTC" });
}

export default function ItemLastUnitCostReport() {
  const { themeId, languageId } = usePmsTheme();
  const t = (text) => reportsText(text, languageId);
  const locale = languageId === "es" ? "es-ES" : "en-GB";
  const user = readStoredUser();
  const [searchParams] = useSearchParams();
  const businessUnitId = String(searchParams.get("business_unit_id") || user?.active_business_unit_id || "");
  const access = usePurchasingReadAccess(businessUnitId);
  const canView = access.can(REPORT_VIEW);
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("all");
  const normalizedSearch = search.trim();
  const filterKey = JSON.stringify([access.contextKey, normalizedSearch, status]);
  const [page, setPage] = useState({ filterKey: "", offset: 0 });
  const offset = page.filterKey === filterKey ? page.offset : 0;
  const queryKey = JSON.stringify([filterKey, offset]);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [refreshToken, setRefreshToken] = useState(0);
  const crossTabRefreshToken = useCrossTabDataRefreshToken();

  useEffect(() => {
    if (!canView || !businessUnitId) {
      setResult(null); setError(""); setLoading(false);
      return undefined;
    }
    const controller = new AbortController();
    setLoading(true); setError(""); setResult(null);
    const timeout = window.setTimeout(async () => {
      try {
        const response = await axios.get("/api/purchasing/reports/item-last-unit-cost", {
          ...access.requestConfig(REPORT_VIEW),
          params: { business_unit_id: businessUnitId, search: normalizedSearch, status, limit: PAGE_SIZE, offset },
          signal: controller.signal,
        });
        if (!controller.signal.aborted && access.can(REPORT_VIEW, true)) {
          const rowCount = Number(response.data?.summary?.rowCount || 0);
          if (offset > 0 && rowCount <= offset) {
            setPage({ filterKey, offset: Math.max(0, Math.floor((rowCount - 1) / PAGE_SIZE) * PAGE_SIZE) });
          } else setResult({ queryKey, data: response.data });
        }
      } catch (requestError) {
        if (controller.signal.aborted) return;
        setError(requestError?.response?.status === 403
          ? "You do not have access to this report."
          : "Last unit cost report could not be loaded.");
      } finally {
        if (!controller.signal.aborted) setLoading(false);
      }
    }, 180);
    return () => { window.clearTimeout(timeout); controller.abort(); };
  }, [businessUnitId, canView, normalizedSearch, status, offset, filterKey, queryKey, refreshToken, crossTabRefreshToken, access]);

  const current = result?.queryKey === queryKey && !error;
  const rows = current && Array.isArray(result?.data?.rows) ? result.data.rows : [];
  const summary = current ? result.data?.summary || {} : {};
  const total = Number(summary.rowCount || 0);
  const waiting = loading || (!current && !error);
  const changePage = (nextOffset) => setPage({ filterKey, offset: nextOffset });
  const hasFilters = Boolean(normalizedSearch) || status !== "all";

  return <div className="pilcore-purchasing-ui ipr-page iluc-page" data-theme={themeId}>
    <div className="ipr-shell">
      <header className="ipr-header">
        <div>
          <span className="ipr-eyebrow"><FiBarChart2 aria-hidden="true" /> {t("Reports")}</span>
          <h1 className="ipr-title">{t("Last unit cost")}</h1>
          <p className="ipr-subtitle">{t("Latest recorded individual cost for each item, without averages.")}</p>
        </div>
        <button type="button" className="ipr-button" disabled={loading || !canView || !businessUnitId}
          onClick={() => setRefreshToken((value) => value + 1)}><FiRefreshCw aria-hidden="true" />{t("Refresh")}</button>
      </header>
      {!canView || !businessUnitId ? <p className="ipr-error" role="alert">
        {t(!businessUnitId ? "Select a business unit to view this report." : "You do not have access to this report.")}
      </p> : <>
        <section className="ipr-toolbar" aria-label={t("Last unit cost filters")}>
          <label className="iluc-search"><FiSearch aria-hidden="true" />
            <input type="search" value={search} maxLength={200} aria-label={t("Search items by name or code")}
              placeholder={t("Search items by name or code")} onChange={(event) => setSearch(event.target.value)} />
          </label>
          <div className="iluc-stateFilters" role="group" aria-label={t("Item status")}>
            {[["all", "All"], ["active", "Active"], ["inactive", "Inactive"]].map(([value, label]) =>
              <button key={value} type="button" className="ipr-button" aria-pressed={status === value}
                onClick={() => setStatus(value)}>{t(label)}</button>)}
          </div>
        </section>
        <p className="iluc-explanation">{t("Last confirmed purchase cost; if there is no purchase cost, the last inventory entry cost. Expressed in the item's inventory unit, without tender prices or FIFO averages.")}</p>
        {error && <div className="ipr-error" role="alert"><span>{t(error)}</span>
          <button type="button" className="ipr-button" onClick={() => setRefreshToken((value) => value + 1)}>{t("Retry")}</button>
        </div>}
        <section className="ipr-stats" aria-label={t("Last unit cost summary")}>
          {[["Items", "rowCount"], ["With cost on this page", "withCostCount"], ["Without cost on this page", "withoutCostCount"]].map(([label, key]) =>
            <div className="ipr-stat" key={key}><span>{t(label)}</span><strong>{current ? Number(summary[key] || 0).toLocaleString(locale) : "—"}</strong></div>)}
        </section>
        <section className="ipr-tableWrap" aria-label={t("Last unit cost")}>
          <div className="ipr-status"><strong>{t("Individual unit costs")}</strong>
            <span role="status">{waiting ? t("Loading...") : error ? "—" :
              `${t("Showing")} ${rows.length ? offset + 1 : 0}–${rows.length ? offset + rows.length : 0} ${t("of")} ${total.toLocaleString(locale)}`}</span>
          </div>
          <div className="ipr-tableScroller" tabIndex={0} aria-label={t("Last unit cost")} aria-busy={waiting}>
            {rows.length ? <table className="ipr-table">
              <thead><tr>{["Item code", "Item", "Category", "Last unit cost", "Unit", "Source", "Date", "Reference", "Supplier", "Status"].map((label) =>
                <th key={label} scope="col" className={label === "Last unit cost" ? "ipr-num" : undefined}>{t(label)}</th>)}</tr></thead>
              <tbody>{rows.map((row) => {
                const hasCost = row.unitCost !== null && row.unitCost !== undefined && String(row.unitCost).trim() !== "" && Number.isFinite(Number(row.unitCost));
                const amount = hasCost ? Number(row.unitCost).toLocaleString(locale, { minimumFractionDigits: 2, maximumFractionDigits: 8 }) : null;
                const date = hasCost ? costDate(row.lastCostDate, locale) : null;
                const unit = getDocumentUnitName({ id: row.unitId, name: row.unitName, code: row.unitCode, name_translations: row.unitNameTranslations }, row.unitId, [], languageId) || row.unitName || row.unitCode || "—";
                return <tr key={row.itemId}>
                  <td>{row.itemCode || "—"}</td><td className="ipr-mainCell">{row.itemName || "—"}</td><td>{row.categoryName || "—"}</td>
                  <td className="ipr-num iluc-cost">{hasCost ? <><strong>{amount} {row.currency || ""}</strong>
                    {row.priceStatus === "estimated" && <span className="iluc-estimated">{t("Estimated")}</span>}</>
                    : <span className="iluc-noCost">{t("No recorded cost")}</span>}</td>
                  <td>{unit}</td><td>{hasCost && SOURCES[row.source] ? <span className="iluc-source">{t(SOURCES[row.source])}</span> : "—"}</td>
                  <td className="iluc-date">{date ? <time dateTime={String(row.lastCostDate).slice(0, 10)}>{date}</time> : "—"}</td>
                  <td>{hasCost ? row.sourceReference || "—" : "—"}</td><td>{hasCost ? row.supplierName || "—" : "—"}</td>
                  <td>{t(row.isActive === false ? "Inactive" : "Active")}</td>
                </tr>;
              })}</tbody>
            </table> : !error && <div className="ipr-empty" role="status"><FiBarChart2 size={28} aria-hidden="true" />
              <strong>{t(waiting ? "Loading items..." : hasFilters ? "No items match the current filters." : "No catalog items yet.")}</strong>
              {!waiting && hasFilters && <span>{t("Try another search or change the filters.")}</span>}
            </div>}
          </div>
          <nav className="iluc-pagination" aria-label={t("Report pages")}>
            <button type="button" className="ipr-button" disabled={waiting || Boolean(error) || offset === 0}
              onClick={() => changePage(Math.max(0, offset - PAGE_SIZE))}><FiArrowLeft aria-hidden="true" />{t("Previous")}</button>
            <button type="button" className="ipr-button" disabled={waiting || Boolean(error) || offset + rows.length >= total}
              onClick={() => changePage(offset + PAGE_SIZE)}>{t("Next")}<FiArrowRight aria-hidden="true" /></button>
          </nav>
        </section>
      </>}
    </div>
  </div>;
}
